import { useState } from 'react';
import {
  AgxBodyCorporate,
  AgxControlledCheckbox,
  AgxFileUpload,
  BodyCorporateDetails,
  CommunityTitlesSchemeDetails,
  CustomPdfScript,
  DocumentTypes,
  EditablePdfButtonGroup,
  EditablePdfDocumentModel,
  Images,
  SellersDisclosureUploadModel,
  UploadedDocument,
} from '@urbanx/agx-ui-components';
import {
  AgxBrowserFilePdfEditor,
  AgxButton,
  AgxDivider,
  AgxDropdown,
  AgxLoadedFileDropzone,
  AgxLoadingState,
  AgxModal,
  AgxSpacer,
  AgxToggleSwitch,
  AgxTypography,
  useToast,
} from '@urbanx/agx.ui-components2';
import { PoolSafetyFileType } from '@urbanx/agx.campaigns.client';
import CommunityTitlesScheme from 'components/community-titles-scheme/CommunityTitlesScheme';
import SurveyPlanUpload from 'components/survey-plan-upload/SurveyPlanUpload';
import { useSellerDisclosurePdf } from './useSellerDisclosurePdf';

import {
  isSellerDisclosureComplianceComplete,
  SellerDisclosureComplianceValues,
} from './sellerDisclosureCompliance';
import * as styles from './SellerDisclosureModal.css';

export interface SellerDisclosureModalValues extends SellerDisclosureComplianceValues {
  sellersDisclosure: SellersDisclosureUploadModel;
}

interface SellerDisclosureModalProps {
  isOpen: boolean;
  campaignId: string;
  initialValues: SellerDisclosureModalValues;
  buttonGroups: EditablePdfButtonGroup[];
  customScript?: CustomPdfScript;
  requiredPdfFields?: string[];
  getAuthToken: () => Promise<string | void>;
  getFileLink?: (authToken: string, filePath: string) => Promise<any>;
  uploadFile?: (...args: any[]) => Promise<any>;
  deleteFile?: (authToken: string, filePath: string) => Promise<number | undefined>;
  setIsDialogOpen: (isOpen: boolean) => void;
  onComplete: (values: SellerDisclosureModalValues) => Promise<void>;
}

const poolSafetyOptions = [
  { label: 'No Pool', value: PoolSafetyFileType.NoPool },
  { label: 'Pool Safety Certificate', value: PoolSafetyFileType.PoolSafetyCertificate },
  { label: 'Building Certificate', value: PoolSafetyFileType.BuildingCertificate },
  { label: 'Form 36', value: PoolSafetyFileType.Form36 },
];

const SellerDisclosureModal = ({
  isOpen,
  campaignId,
  initialValues,
  buttonGroups,
  customScript = CustomPdfScript.None,
  requiredPdfFields = [],
  getAuthToken,
  getFileLink,
  uploadFile,
  deleteFile,
  setIsDialogOpen,
  onComplete,
}: SellerDisclosureModalProps) => {
  const { pushToast } = useToast();
  const [values, setValues] = useState({
    ...initialValues,
    allVendorsOrSolicitorSigned: false,
    poolSafetyFileType: initialValues.poolSafetyFileType ?? PoolSafetyFileType.NoPool,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const existingDocument = initialValues.sellersDisclosure.sellersDisclosureDocuments?.[0];
  const {
    loadedFile,
    editState,
    mappedFields,
    reviewSource,
    mappingFieldGroups,
    isPreparing,
    allRequiredFieldsMapped,
    isMappingStep,
    handleFileLoaded,
    handleEditStateChange,
    handleMappingConfirmed,
    backToMapping,
  } = useSellerDisclosurePdf({
    buttonGroups,
    customScript,
    requiredPdfFields,
    getAuthToken,
  });

  const complianceComplete = isSellerDisclosureComplianceComplete(values);

  const handleComplete = async () => {
    if (!loadedFile || !reviewSource?.arrayBuffer || !complianceComplete || !uploadFile) return;
    setIsSubmitting(true);
    try {
      const authToken = await getAuthToken();
      if (!authToken) throw new Error('Unable to upload PDF: authentication is not configured.');
      const file = new File([reviewSource.arrayBuffer], loadedFile.fileName, {
        type: 'application/pdf',
        lastModified: loadedFile.file.lastModified,
      });
      const formData = new FormData();
      formData.append('File', file, file.name);
      const uploadedDocument = (await uploadFile(
        authToken,
        formData,
        campaignId,
        DocumentTypes.SellerDisclosure
      )) as UploadedDocument | undefined;
      if (!uploadedDocument) throw new Error('Unable to upload Seller Disclosure PDF.');

      await onComplete({
        ...values,
        sellersDisclosure: {
          markAsExempt: false,
          allVendorsOrSolicitorSigned: values.allVendorsOrSolicitorSigned,
          sellersDisclosureDocuments: [
            {
              uploadedDocument,
              documentFields: mappedFields,
            } satisfies EditablePdfDocumentModel,
          ],
        },
      });
      // Only remove stored files after their replacement references have been saved.
      const documentsToDelete = [existingDocument?.uploadedDocument];
      if (values.surveyPlan?.surveyPlansAttachedToSellerDisclosure) {
        documentsToDelete.push(...(initialValues.surveyPlan?.surveyPlans ?? []));
      }
      if (values.poolSafetyAttachedToSellerDisclosure) {
        documentsToDelete.push(
          ...(initialValues.poolSafetyCertificatePDF ?? []),
          ...(initialValues.buildingCertificatePDF ?? []),
          ...(initialValues.poolSafetyForm36PDF ?? [])
        );
      }
      if (values.communityTitlesSchemeDetails?.statementsAttachedToSellerDisclosure) {
        documentsToDelete.push(
          ...(initialValues.communityTitlesSchemeDetails?.communityManagementStatements ?? [])
        );
      }
      if (values.bodyCorporateDetails?.certificateAttachedToSellerDisclosure) {
        documentsToDelete.push(initialValues.bodyCorporateDetails?.bodyCorporateCertificate);
      }
      for (const filePath of new Set(
        documentsToDelete.map((document) => document?.containerFilePath)
      )) {
        if (filePath && filePath !== uploadedDocument.containerFilePath) {
          await deleteFile?.(authToken, filePath);
        }
      }
      setIsDialogOpen(false);
    } catch {
      pushToast({ type: 'error', message: 'Unable to save Seller Disclosure' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const poolUpload = (() => {
    if (values.poolSafetyFileType === PoolSafetyFileType.PoolSafetyCertificate)
      return {
        id: 'PoolSafetyCertificatePDF',
        title: 'Pool Safety Certificate',
        type: DocumentTypes.PoolCertificate,
        documents: values.poolSafetyCertificatePDF,
        update: (documents: UploadedDocument[]) =>
          setValues((current) => ({ ...current, poolSafetyCertificatePDF: documents })),
      };
    if (values.poolSafetyFileType === PoolSafetyFileType.BuildingCertificate)
      return {
        id: 'BuildingCertificatePDF',
        title: 'Building Certificate',
        type: DocumentTypes.BuildingCertificate,
        documents: values.buildingCertificatePDF,
        update: (documents: UploadedDocument[]) =>
          setValues((current) => ({ ...current, buildingCertificatePDF: documents })),
      };
    if (values.poolSafetyFileType === PoolSafetyFileType.Form36)
      return {
        id: 'PoolSafetyForm36PDF',
        title: 'Signed Form 36',
        type: DocumentTypes.NoPoolCertificate,
        documents: values.poolSafetyForm36PDF,
        update: (documents: UploadedDocument[]) =>
          setValues((current) => ({ ...current, poolSafetyForm36PDF: documents })),
      };
    return null;
  })();

  const onPoolSafetyAttachedToSellerDisclosureChanged = (checked: boolean) => {
    setValues((current) => ({
      ...current,
      poolSafetyAttachedToSellerDisclosure: checked,
      poolSafetyCertificatePDF: checked ? undefined : current.poolSafetyCertificatePDF,
      buildingCertificatePDF: checked ? undefined : current.buildingCertificatePDF,
      poolSafetyForm36PDF: checked ? undefined : current.poolSafetyForm36PDF,
    }));
  };

  return (
    <AgxModal
      type="fullHeight"
      variant="withDivider"
      actionButtonContainerStyle="withDivider"
      className={styles.modal}
      minWidth="0"
      secondaryTitle={
        <AgxSpacer direction="vertical" gap="spacing-2xl" fill padding={{ bottom: 'spacing-xl' }}>
          <AgxTypography id="sellerDisclosureModalTitle" type="body_ui_strong" size="medium">
            {isMappingStep
              ? 'Step 1: Upload Seller Disclosure'
              : 'Step 2: Seller Disclosure Review'}
          </AgxTypography>
        </AgxSpacer>
      }
      content={
        <AgxSpacer
          data-testid="modal-sellerDisclosure"
          className={styles.content}
          direction="vertical"
          gap="spacing-xl"
          fill
          padding={{
            top: isMappingStep ? 'spacing-5xl' : 'spacing-3xl',
            bottom: isMappingStep ? 'spacing-5xl' : 'none',
            left: 'spacing-5xl',
            right: 'spacing-5xl',
          }}
        >
          {isMappingStep ? (
            <div className={styles.mapping}>
              {isPreparing ? (
                <div className={styles.loading}>
                  <AgxLoadingState className={styles.loadingState} />
                </div>
              ) : !loadedFile ? (
                <div className={styles.dropzone}>
                  <AgxLoadedFileDropzone
                    id="sellerDisclosureUpload"
                    dataTestId="seller-disclosure-upload-dropzone"
                    accept=".pdf"
                    extensions={['pdf']}
                    multiple={false}
                    loadedFile={loadedFile}
                    disabled={isSubmitting}
                    onFileLoaded={(file) => {
                      setValues((current) => ({ ...current, allVendorsOrSolicitorSigned: false }));
                      void handleFileLoaded(file);
                    }}
                    fillVertical
                  />
                </div>
              ) : null}
              {!isPreparing && loadedFile && editState ? (
                <div className={styles.editor}>
                  <AgxBrowserFilePdfEditor
                    id="sellerDisclosurePdfEditor"
                    className={styles.pdfEditor}
                    source={loadedFile}
                    pdfFileName={loadedFile.fileName}
                    initialEditState={editState}
                    initialPageIndex={6}
                    mappingFieldGroups={mappingFieldGroups}
                    showAddText={false}
                    showStrikeOut={false}
                    showFieldBlockTools={false}
                    toolsPosition="right"
                    groupMappingFields
                    onChange={handleEditStateChange}
                  />
                </div>
              ) : null}
            </div>
          ) : (
            <div className={styles.compliance}>
              <div className={styles.preview}>
                <AgxBrowserFilePdfEditor
                  id="sellerDisclosurePdfPreview"
                  readOnly
                  source={reviewSource}
                  pdfFileName={reviewSource?.fileName}
                  showToolbar={false}
                  showEditOverlays={false}
                />
              </div>
              <AgxSpacer className={styles.controls} direction="vertical" gap="spacing-xl" fill>
                <AgxTypography
                  id="sellerDisclosureConfirmSignatures"
                  type="body_ui_strong"
                  size="small"
                  role="heading"
                  aria-level={2}
                >
                  Confirm Signatures
                </AgxTypography>
                <AgxSpacer
                  direction="horizontal"
                  gap="spacing-lg"
                  fill
                  contentJustification="space-between"
                >
                  <AgxTypography id="sellerDisclosureSigned" type="body_ui" size="medium">
                    All Vendors/Vendors&apos; Solicitor signed the Seller Disclosure
                  </AgxTypography>
                  <AgxToggleSwitch
                    id="toggle-sellerDisclosureSigned"
                    checked={values.allVendorsOrSolicitorSigned}
                    onChange={(checked) =>
                      setValues((current) => ({
                        ...current,
                        allVendorsOrSolicitorSigned: checked,
                      }))
                    }
                  />
                </AgxSpacer>
                <AgxDivider variant="horizontal" />
                <SurveyPlanUpload
                  id="SurveyPlan"
                  label="Survey Plan"
                  campaignId={campaignId}
                  defaultValue={values.surveyPlan}
                  onChange={({ value }) =>
                    setValues((current) => ({ ...current, surveyPlan: value }))
                  }
                  getAuthToken={getAuthToken}
                  uploadFile={uploadFile}
                  deleteFile={deleteFile}
                  getFileLink={getFileLink}
                  required
                  validate
                  multiple
                />
                <AgxDivider variant="horizontal" />
                <AgxDropdown
                  id="PoolSafetyFileType"
                  label="Pool Safety"
                  type="single"
                  options={poolSafetyOptions}
                  selected={values.poolSafetyFileType ?? PoolSafetyFileType.NoPool}
                  onChange={(value) =>
                    setValues((current) => ({
                      ...current,
                      poolSafetyFileType: value as PoolSafetyFileType,
                      poolSafetyAttachedToSellerDisclosure:
                        value === PoolSafetyFileType.NoPool
                          ? false
                          : current.poolSafetyAttachedToSellerDisclosure,
                    }))
                  }
                  required
                  hideOptionalText
                />
                {poolUpload ? (
                  <AgxFileUpload
                    key={poolUpload.id}
                    id={poolUpload.id}
                    title={poolUpload.title}
                    campaignId={campaignId}
                    documentType={poolUpload.type}
                    fileData={poolUpload.documents}
                    onValueChanged={({ value }) => poolUpload.update(value)}
                    getAuthToken={getAuthToken}
                    uploadFile={uploadFile}
                    deleteFile={deleteFile}
                    getFileLink={getFileLink}
                    extensions={['pdf']}
                    required={!values.poolSafetyAttachedToSellerDisclosure}
                    validate
                    disabled={values.poolSafetyAttachedToSellerDisclosure ?? false}
                    uploadButtonIcon={<Images.CloudUpload />}
                  />
                ) : null}
                {values.poolSafetyFileType !== PoolSafetyFileType.NoPool ? (
                  <AgxControlledCheckbox
                    id="PoolSafetyAttachedToSellerDisclosure"
                    checked={values.poolSafetyAttachedToSellerDisclosure ?? false}
                    onValueChanged={onPoolSafetyAttachedToSellerDisclosureChanged}
                    label="Attached to Seller Disclosure"
                    naked
                  />
                ) : null}
                <AgxDivider variant="horizontal" />
                <CommunityTitlesScheme
                  id="CommunityTitlesSchemeDetails"
                  label="Is the property part of a Community Titles Scheme?"
                  campaignId={campaignId}
                  defaultValue={values.communityTitlesSchemeDetails}
                  onChange={({ value }) =>
                    setValues((current) => ({
                      ...current,
                      communityTitlesSchemeDetails: value as CommunityTitlesSchemeDetails,
                    }))
                  }
                  getAuthToken={getAuthToken}
                  uploadFile={uploadFile}
                  deleteFile={deleteFile}
                  getFileLink={getFileLink}
                  required
                  validate
                  multiple
                />
                <AgxDivider variant="horizontal" />
                <AgxBodyCorporate
                  id="BodyCorporateDetails"
                  label="Body Corporate"
                  campaignId={campaignId}
                  defaultValue={values.bodyCorporateDetails}
                  onValueChanged={({ value }) =>
                    setValues((current) => ({
                      ...current,
                      bodyCorporateDetails: value as BodyCorporateDetails,
                    }))
                  }
                  getAuthToken={getAuthToken}
                  uploadFile={uploadFile}
                  deleteFile={deleteFile}
                  getFileLink={getFileLink}
                  required
                  validate
                  showUploadDocument
                />
              </AgxSpacer>
            </div>
          )}
        </AgxSpacer>
      }
      actionButtons={
        <AgxSpacer
          direction="horizontal"
          gap="spacing-md"
          fill
          contentAlignment="center"
          contentJustification={isMappingStep ? 'center' : 'end'}
        >
          {isMappingStep ? (
            <>
              <AgxButton
                id="sellerDisclosureMappingConfirmed"
                dataTestId="btn-sellerDisclosureMappingConfirmed"
                text={isPreparing ? 'Preparing preview...' : 'All fields are mapped correctly'}
                variant="primary"
                size="large"
                onClick={handleMappingConfirmed}
                disabled={!loadedFile || !editState || !allRequiredFieldsMapped || isPreparing}
              />
            </>
          ) : (
            <>
              <AgxButton
                id="sellerDisclosureBack"
                text="Back"
                variant="hollow"
                size="large"
                onClick={backToMapping}
                disabled={isSubmitting}
              />
              <span title={!complianceComplete ? 'Missing required information' : undefined}>
                <AgxButton
                  id="sellerDisclosureComplete"
                  dataTestId="btn-sellerDisclosureComplete"
                  text={isSubmitting ? 'Saving...' : 'Complete'}
                  variant="primary"
                  size="large"
                  onClick={handleComplete}
                  disabled={!complianceComplete || isSubmitting}
                />
              </span>
            </>
          )}
        </AgxSpacer>
      }
      setIsDialogOpen={(open) => {
        if (!open && !isSubmitting) setIsDialogOpen(false);
      }}
      isOpen={isOpen}
    />
  );
};

export default SellerDisclosureModal;
